import { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../business/network/ApiClient";

const DEBOUNCE_MS = 600;
const MIN_TOKENS = 3;
const CONFIDENCE_THRESHOLD = 0.50;
// Minimum candidate similarity to show in the popover.
// Detections where ALL candidates fall below this are not highlighted at all.
const SIMILARITY_THRESHOLD = 0.45;

// Common stop-word tokens unlikely to contain named entities — mirrors backend pre-filter logic
const STOP_WORDS = new Set([
  "the", "a", "an", "is", "are", "was", "were", "be", "been", "being",
  "have", "has", "had", "do", "does", "did", "will", "would", "could",
  "should", "may", "might", "shall", "can", "to", "of", "in", "on", "at",
  "by", "for", "with", "and", "but", "or", "nor", "so", "yet", "if",
  "how", "what", "who", "which", "when", "where", "show", "me", "my",
  "get", "give", "list", "tell", "find",
]);

/**
 * Cheap pre-filter — saves an unnecessary API call when the sentence clearly contains
 * no named entities (too short, or entirely stop words).
 * Intentionally lenient: requires 3+ tokens with at least one meaningful non-stop-word.
 * Uppercase/digit check is left to the backend orchestrator which is the authoritative gate.
 */
function passesPreFilter(sentence) {
  if (!sentence || sentence.trim().length === 0) return false;
  const tokens = sentence.trim().split(/[\s,.:;!?]+/).filter(Boolean);
  if (tokens.length < MIN_TOKENS) return false;

  // At least one token must be a non-stop-word with more than one character
  return tokens.some(
    (t) => t.length > 1 && !STOP_WORDS.has(t.toLowerCase())
  );
}

/**
 * useNounRecognition — debounced noun recognition hook.
 *
 * @param {string} text      The current input text.
 * @param {number} userId    Current user ID.
 * @param {number} ouId      Current OU ID.
 * @returns {{ detections: NounDetection[], isScanning: boolean }}
 *
 * Each NounDetection: { span, startIndex, endIndex, nounType, confidence, candidates[] }
 */
export function useNounRecognition(text, userId, ouId, paused = false) {
  const [detections, setDetections] = useState([]);
  const [isScanning, setIsScanning] = useState(false);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  // Track the last text that was actually scanned to avoid redundant calls
  const lastScannedRef = useRef("");

  /**
   * Called when the user selects a replacement candidate from the popover.
   * Removes the replaced detection from state, shifts the startIndex/endIndex of
   * every detection that follows the replacement point by the length delta, and
   * advances lastScannedRef so no redundant re-scan fires.
   */
  const updateAfterReplace = useCallback((replacedDetection, candidateText, newFullText) => {
    const originalLen = replacedDetection.endIndex - replacedDetection.startIndex;
    const delta       = candidateText.length - originalLen;

    setDetections((prev) =>
      prev
        // Remove the replaced detection
        .filter(
          (d) =>
            !(d.span === replacedDetection.span && d.nounType === replacedDetection.nounType)
        )
        // Shift indices of all detections that start after the replaced span
        .map((d) => {
          if (delta !== 0 && d.startIndex >= replacedDetection.endIndex) {
            return { ...d, startIndex: d.startIndex + delta, endIndex: d.endIndex + delta };
          }
          return d;
        })
    );
    // Suppress the next re-scan — the replacement is an intentional user action.
    lastScannedRef.current = newFullText;
  }, []);

  /**
   * Called after a mention (@query) selection is applied.
   * Clear lastScannedRef so the post-mention scan fires and picks up
   * entities in the updated text (including the inserted mention text).
   */
  const updateAfterMention = useCallback((_newFullText) => {
    lastScannedRef.current = "";
  }, []);

  const scan = useCallback(
    async (sentence) => {
      if (!passesPreFilter(sentence)) {
        setDetections([]);
        return;
      }

      if (sentence.trim() === lastScannedRef.current.trim()) return;

      // Cancel any in-flight scan
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsScanning(true);
      try {
        const response = await apiClient.scanNouns(
          sentence,
          userId,
          ouId,
          controller.signal
        );

        if (controller.signal.aborted) return;

        // Only lock this sentence as "already scanned" when we got a non-empty result.
        // If the LLM returned nothing (after retry), leave lastScannedRef unchanged so
        // a future text change (even just one character) still triggers a fresh scan.
        const qualified = (response?.detections ?? [])
          .filter((d) => d.confidence >= CONFIDENCE_THRESHOLD)
          .map((d) => ({
            ...d,
            candidates: (d.candidates ?? []).filter(
              (c) => c.similarity >= SIMILARITY_THRESHOLD
            ),
          }))
          // Drop detections where no candidate meets the similarity bar
          .filter((d) => d.candidates.length > 0)
          // Drop detections where the user already typed the exact entity name —
          // an exact text match means the intent is valid, no correction needed.
          .filter(
            (d) =>
              !d.candidates.some(
                (c) =>
                  c.text.trim().toLowerCase() === (d.span ?? "").trim().toLowerCase()
              )
          );

        if (qualified.length > 0) {
          lastScannedRef.current = sentence;
        }
        setDetections(qualified);
      } catch {
        if (!controller.signal.aborted) {
          setDetections([]);
        }
      } finally {
        // Always clear the scanning indicator — even on abort, the scan is no longer active.
        // The next scan will set it true again when it starts.
        setIsScanning(false);
      }
    },
    [userId, ouId]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);

    // Cancel any in-flight request immediately when text changes —
    // don't wait for the debounce to fire before aborting.
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    if (!text || userId == null || ouId == null) {
      setDetections([]);
      lastScannedRef.current = "";
      return;
    }

    // While @mention mode is active, keep existing detections — they're still valid
    // for the rest of the phrase. The scan still runs so other entities aren't missed.
    if (!paused && text.trim() !== lastScannedRef.current.trim()) {
      setDetections([]);
    }

    debounceRef.current = setTimeout(() => scan(text), DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [text, userId, ouId, scan, paused]);

  return { detections, isScanning, updateAfterReplace, updateAfterMention };
}
